/**
 * Tagada Rx — guided onboarding journey (V2).
 *
 * One vertical checklist owns the whole path from "never heard of Rx"
 * to "storefront live": Apply → Approval → Get paid (KYC + bank) →
 * Launch. The merchant always sees where they are, what is left, and
 * how long each step takes.
 *
 * Key ergonomic decision: payout setup (KYC + bank account) unlocks the
 * moment the application is submitted, so the merchant finishes it
 * *while* we review — by the time the application is accepted there is
 * nothing left between them and their storefront.
 */

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Clock4,
  CreditCard,
  ExternalLink,
  Globe,
  Landmark,
  Loader2,
  Lock,
  Mail,
  PartyPopper,
  Rocket,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Wallet,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { useAccountManager } from '@/hooks/use-account-manager';
import {
  formatCents,
  useRxActivationStatus,
  useRxConfirmSetupFeePaid,
  useRxIntegrationStatus,
  useRxPayoutProfile,
  useRxRequestActivation,
  useRxStorefront,
} from '@/lib/api/rx';

import { RxBankAccountForm } from './rx-bank-account-form';
import { RxKycStep } from './rx-kyc-step';
import { RxPaymentsStatusBadge } from './rx-status-badge';

type StepState = 'done' | 'current' | 'available' | 'locked';

// ---------------------------------------------------------------------------
// Step rail primitives
// ---------------------------------------------------------------------------

function StepMarker({ state, index }: { state: StepState; index: number }) {
  if (state === 'done') {
    return (
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm">
        <Check className="h-4 w-4" strokeWidth={3} />
      </div>
    );
  }
  if (state === 'current' || state === 'available') {
    return (
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold',
          state === 'current'
            ? 'bg-primary text-primary-foreground ring-primary/20 shadow-md ring-4'
            : 'border-primary/40 text-primary bg-primary/5 border-2',
        )}
      >
        {index}
      </div>
    );
  }
  return (
    <div className="bg-muted text-muted-foreground flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
      <Lock className="h-3.5 w-3.5" />
    </div>
  );
}

function StepChip({ state, label }: { state: StepState; label?: string }) {
  const { t } = useTranslation('rx');
  if (label) {
    return (
      <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
        {label}
      </Badge>
    );
  }
  switch (state) {
    case 'done':
      return (
        <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
          {t('onboardingJourney.chip.done')}
        </Badge>
      );
    case 'current':
      return <Badge>{t('onboardingJourney.chip.current')}</Badge>;
    case 'available':
      return (
        <Badge variant="outline" className="border-primary/30 text-primary">
          {t('onboardingJourney.chip.available')}
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-muted-foreground">
          {t('onboardingJourney.chip.locked')}
        </Badge>
      );
  }
}

function JourneyStep({
  index,
  isLast,
  state,
  chipLabel,
  title,
  subtitle,
  eta,
  children,
}: {
  index: number;
  isLast?: boolean;
  state: StepState;
  chipLabel?: string;
  title: string;
  subtitle: string;
  eta?: string;
  children?: React.ReactNode;
}) {
  const open = children != null && state !== 'done' && state !== 'locked';
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <StepMarker state={state} index={index} />
        {!isLast ? (
          <div
            className={cn(
              'my-1 w-px flex-1',
              state === 'done' ? 'bg-emerald-300' : 'bg-border',
            )}
          />
        ) : null}
      </div>
      <div className={cn('min-w-0 flex-1', !isLast && 'pb-6')}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'text-sm font-semibold',
                state === 'locked' && 'text-muted-foreground',
              )}
            >
              {title}
            </span>
            {eta && state !== 'done' ? (
              <span className="text-muted-foreground text-xs">· {eta}</span>
            ) : null}
          </div>
          <StepChip state={state} label={chipLabel} />
        </div>
        <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">{subtitle}</p>
        {open ? <div className="mt-3">{children}</div> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Apply (plan choice, single primary CTA)
// ---------------------------------------------------------------------------

const AFFILIATE_POINT_KEYS = [
  'onboardingJourney.step1.points.brand',
  'onboardingJourney.step1.points.compliance',
  'onboardingJourney.step1.points.clinicians',
  'onboardingJourney.step1.points.templates',
] as const;

function ApplyStepContent({
  onApply,
  pending,
}: {
  onApply: () => void;
  pending: boolean;
}) {
  const { t } = useTranslation('rx');
  return (
    <div className="space-y-3">
      <div className="border-primary/25 bg-primary/[0.03] rounded-xl border p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Badge>{t('onboardingJourney.step1.badge.affiliate')}</Badge>
              <span className="text-muted-foreground text-xs font-medium">
                {t('onboardingJourney.step1.badge.setup')}
              </span>
            </div>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span className="text-3xl font-semibold tracking-tight">
                {t('onboardingJourney.step1.rate')}
              </span>
              <span className="text-muted-foreground text-sm">
                {t('onboardingJourney.step1.rateNote')}
              </span>
            </div>
            <p className="text-muted-foreground mt-1 text-xs">{t('onboardingJourney.step1.noFees')}</p>
          </div>
          <Button onClick={onApply} disabled={pending} size="lg" className="shadow-sm">
            {pending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CreditCard className="mr-2 h-4 w-4" />
            )}
            {t('onboardingJourney.step1.apply')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {AFFILIATE_POINT_KEYS.map((key) => (
            <li key={key} className="flex items-start gap-2 text-xs">
              <Check className="text-primary mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{t(key)}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="text-muted-foreground flex items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-xs">
        <Sparkles className="h-3.5 w-3.5 shrink-0" />
        <span>
          <span className="text-foreground font-medium">
            {t('onboardingJourney.step1.standaloneStrong')}
          </span>
          {t('onboardingJourney.step1.standaloneRest')}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Application review (timeline + staff unlock)
// ---------------------------------------------------------------------------

function ReviewTimelineRow({
  icon: Icon,
  title,
  detail,
  state,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  detail: string;
  state: 'done' | 'active' | 'todo';
}) {
  return (
    <div className="flex items-start gap-3">
      <div
        className={cn(
          'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
          state === 'done' && 'bg-emerald-100 text-emerald-700',
          state === 'active' && 'bg-amber-100 text-amber-700',
          state === 'todo' && 'bg-muted text-muted-foreground',
        )}
      >
        {state === 'done' ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
      </div>
      <div>
        <div
          className={cn(
            'text-xs font-semibold',
            state === 'todo' && 'text-muted-foreground font-medium',
          )}
        >
          {title}
        </div>
        <div className="text-muted-foreground text-xs">{detail}</div>
      </div>
    </div>
  );
}

function ReviewStepContent({
  requestedAt,
  setupFeeLabel,
  isAccountManager,
  onMarkPaid,
  markPaidPending,
}: {
  requestedAt: string | null;
  setupFeeLabel: string;
  isAccountManager: boolean;
  onMarkPaid: () => void;
  markPaidPending: boolean;
}) {
  const { t } = useTranslation('rx');
  return (
    <div className="space-y-3 rounded-xl border p-4">
      <div className="space-y-3">
        <ReviewTimelineRow
          icon={Check}
          state="done"
          title={t('onboardingJourney.step2.review.received.title')}
          detail={
            requestedAt
              ? t('onboardingJourney.step2.review.received.detailWithDate', {
                  date: new Date(requestedAt).toLocaleDateString(),
                  fee: setupFeeLabel,
                })
              : t('onboardingJourney.step2.review.received.detailDefault', { fee: setupFeeLabel })
          }
        />
        <ReviewTimelineRow
          icon={Clock4}
          state="active"
          title={t('onboardingJourney.step2.review.underReview.title')}
          detail={t('onboardingJourney.step2.review.underReview.detail')}
        />
        <ReviewTimelineRow
          icon={Mail}
          state="todo"
          title={t('onboardingJourney.step2.review.greenLight.title')}
          detail={t('onboardingJourney.step2.review.greenLight.detail')}
        />
      </div>
      {isAccountManager ? (
        <div className="border-t pt-3">
          <Button onClick={onMarkPaid} disabled={markPaidPending} variant="outline" size="sm">
            {markPaidPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="mr-2 h-4 w-4" />
            )}
            {t('onboardingJourney.step2.review.staffApprove')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — Launch storefront
// ---------------------------------------------------------------------------

function LaunchStepContent({ published, url }: { published: boolean; url: string | null }) {
  const { t } = useTranslation('rx');
  const { storeRoutes } = useStoreNavigate();
  if (published && url) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
        <div className="flex items-center gap-3">
          <Globe className="h-5 w-5 shrink-0 text-emerald-600" />
          <div className="text-sm">
            <div className="font-semibold text-emerald-800">
              {t('onboardingJourney.step4.live.title')}
            </div>
            <div className="text-xs text-emerald-700">{url.replace('https://', '')}</div>
          </div>
        </div>
        <Button asChild size="sm" variant="outline">
          <a href={url} target="_blank" rel="noreferrer">
            {t('onboardingJourney.step4.visit')}
            <ExternalLink className="ml-2 h-3.5 w-3.5" />
          </a>
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4">
      <div className="flex items-center gap-3">
        <Rocket className="text-primary h-5 w-5 shrink-0" />
        <div className="text-sm">
          <div className="font-semibold">{t('onboardingJourney.step4.default.title')}</div>
          <div className="text-muted-foreground text-xs">
            {t('onboardingJourney.step4.default.body')}
          </div>
        </div>
      </div>
      <Button asChild size="sm">
        <Link to={storeRoutes.RX_STOREFRONT()}>
          {t('onboardingJourney.step4.default.cta')}
          <ArrowRight className="ml-2 h-3.5 w-3.5" />
        </Link>
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Journey
// ---------------------------------------------------------------------------

export function RxOnboardingJourney({ storeId }: { storeId: string }) {
  const { t } = useTranslation('rx');
  const { toast } = useToast();
  const { isAccountManager } = useAccountManager();
  const statusQuery = useRxIntegrationStatus(storeId);
  const requestActivation = useRxRequestActivation();
  const confirmPaid = useRxConfirmSetupFeePaid();

  const status = statusQuery.data;
  const applied = status != null && status.state !== 'not_requested';
  const active = status?.state === 'active';

  const profileQuery = useRxPayoutProfile({ poll: applied });
  const profile = profileQuery.data;
  const payoutsReady = profile?.payoutsReady === true;
  const kycCleared = profile?.kycCleared === true;

  // Payment readiness is a status only (no step): shown once approved.
  const activationQuery = useRxActivationStatus(storeId, { enabled: active });
  const payments = activationQuery.data?.payments ?? null;
  const partnerBlocked = activationQuery.data?.missing.includes('partner_rx_option') ?? false;

  const storefrontQuery = useRxStorefront(storeId);
  const published = storefrontQuery.data?.published === true;
  const storefrontUrl = storefrontQuery.data?.url ?? null;

  if (statusQuery.isLoading || !status) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Stethoscope className="text-primary h-4 w-4" />
            {t('overview.title')}
          </CardTitle>
          <CardDescription>{t('onboardingJourney.loading')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const handleApply = async () => {
    try {
      await requestActivation.mutateAsync({
        storeId,
        operatingMode: 'affiliate',
        setupFeeAmountMinor: 0,
        setupFeeCurrency: 'USD',
      });
      toast({
        title: t('onboardingJourney.toast.submitted.title'),
        description: t('onboardingJourney.toast.submitted.description'),
      });
      statusQuery.refetch();
    } catch (err) {
      toast({
        title: t('onboardingJourney.toast.submitFailed.title'),
        description: err instanceof Error ? err.message : t('onboardingJourney.toast.unknownError'),
        variant: 'destructive',
      });
    }
  };

  const handleMarkPaid = async () => {
    try {
      await confirmPaid.mutateAsync({ storeId });
      toast({
        title: t('onboardingJourney.toast.approved.title'),
        description: t('onboardingJourney.toast.approved.description'),
      });
      statusQuery.refetch();
    } catch (err) {
      toast({
        title: t('onboardingJourney.toast.approveFailed.title'),
        description: err instanceof Error ? err.message : t('onboardingJourney.toast.unknownError'),
        variant: 'destructive',
      });
    }
  };

  const doneCount = [applied, active, payoutsReady, published].filter(Boolean).length;
  const allDone = doneCount === 4;

  if (allDone) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
        <div className="flex items-center gap-3">
          <PartyPopper className="h-5 w-5 shrink-0 text-emerald-600" />
          <div className="text-sm">
            <span className="font-semibold text-emerald-800">
              {t('onboardingJourney.completeBanner.headline')}
            </span>{' '}
            <span className="text-emerald-700">
              {storefrontUrl
                ? t('onboardingJourney.completeBanner.bodyWithHost', {
                    host: storefrontUrl.replace('https://', ''),
                  })
                : t('onboardingJourney.completeBanner.body')}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {payments ? <RxPaymentsStatusBadge payments={payments} partnerBlocked={partnerBlocked} /> : null}
          {storefrontUrl ? (
            <Button asChild size="sm" variant="outline">
              <a href={storefrontUrl} target="_blank" rel="noreferrer">
                {t('onboardingJourney.completeBanner.visit')}
                <ExternalLink className="ml-2 h-3.5 w-3.5" />
              </a>
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  const setupFeeLabel = formatCents(
    status.state === 'not_requested' ? 0 : status.settings.setupFeeAmountMinor,
    status.state === 'not_requested' ? 'USD' : status.settings.setupFeeCurrency,
  );

  const applyState: StepState = applied ? 'done' : 'current';
  const approvalState: StepState = active ? 'done' : applied ? 'current' : 'locked';
  const payoutState: StepState = payoutsReady
    ? 'done'
    : applied
      ? active
        ? 'current'
        : 'available'
      : 'locked';
  const launchState: StepState = published ? 'done' : active ? (payoutsReady ? 'current' : 'available') : 'locked';

  return (
    <Card>
      <CardHeader className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Stethoscope className="text-primary h-4 w-4" />
              {active
                ? t('onboardingJourney.header.titleActive')
                : t('onboardingJourney.header.titleDefault')}
            </CardTitle>
            <CardDescription className="mt-1">
              {active
                ? t('onboardingJourney.header.descriptionActive')
                : t('onboardingJourney.header.descriptionDefault')}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {active && payments ? (
              <RxPaymentsStatusBadge payments={payments} partnerBlocked={partnerBlocked} />
            ) : null}
            <div className="text-muted-foreground text-xs font-medium">
              {t('onboardingJourney.header.completeCount', { done: doneCount, total: 4 })}
            </div>
          </div>
        </div>
        <Progress value={(doneCount / 4) * 100} className="h-1.5" />
      </CardHeader>
      <CardContent>
        <JourneyStep
          index={1}
          state={applyState}
          title={t('onboardingJourney.step1.title')}
          subtitle={t('onboardingJourney.step1.subtitle')}
          eta={t('onboardingJourney.step1.eta')}
        >
          <ApplyStepContent onApply={() => void handleApply()} pending={requestActivation.isPending} />
        </JourneyStep>

        <JourneyStep
          index={2}
          state={approvalState}
          chipLabel={approvalState === 'current' ? t('onboardingJourney.chip.inReview') : undefined}
          title={t('onboardingJourney.step2.title')}
          subtitle={
            active
              ? t('onboardingJourney.step2.subtitleActive')
              : t('onboardingJourney.step2.subtitleDefault')
          }
          eta={t('onboardingJourney.step2.eta')}
        >
          {status.state === 'pending_payment' ? (
            <ReviewStepContent
              requestedAt={status.settings.setupFeeRequestedAt ?? null}
              setupFeeLabel={setupFeeLabel}
              isAccountManager={isAccountManager}
              onMarkPaid={() => void handleMarkPaid()}
              markPaidPending={confirmPaid.isPending}
            />
          ) : null}
        </JourneyStep>

        <JourneyStep
          index={3}
          state={payoutState}
          title={t('onboardingJourney.step3.title')}
          subtitle={
            payoutState === 'available'
              ? t('onboardingJourney.step3.subtitleAvailable')
              : t('onboardingJourney.step3.subtitleDefault')
          }
          eta={t('onboardingJourney.step3.eta')}
        >
          {profile ? (
            <div className="space-y-4 rounded-xl border p-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <ShieldCheck
                    className={cn('h-4 w-4', kycCleared ? 'text-emerald-600' : 'text-primary')}
                  />
                  <span className="text-sm font-semibold">
                    {t('onboardingJourney.step3.verifyLabel')}
                  </span>
                  {kycCleared ? (
                    <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                      {t('onboardingJourney.step3.badgeVerified')}
                    </Badge>
                  ) : null}
                </div>
                <RxKycStep profile={profile} onChanged={() => profileQuery.refetch()} />
              </div>
              <div className="space-y-2 border-t pt-4">
                <div className="flex items-center gap-2">
                  <Landmark
                    className={cn(
                      'h-4 w-4',
                      profile.bankReady ? 'text-emerald-600' : kycCleared ? 'text-primary' : 'text-muted-foreground',
                    )}
                  />
                  <span className="text-sm font-semibold">
                    {t('onboardingJourney.step3.bankLabel')}
                  </span>
                  {profile.bankReady ? (
                    <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                      {t('onboardingJourney.step3.badgeSaved')}
                    </Badge>
                  ) : null}
                </div>
                {kycCleared ? (
                  <RxBankAccountForm profile={profile} onSaved={() => profileQuery.refetch()} />
                ) : (
                  <p className="text-muted-foreground text-xs">
                    {t('onboardingJourney.step3.gateHint')}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="text-muted-foreground flex items-center gap-2 rounded-xl border p-4 text-xs">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('onboardingJourney.step3.loadingProfile')}
            </div>
          )}
        </JourneyStep>

        <JourneyStep
          index={4}
          isLast
          state={launchState}
          title={t('onboardingJourney.step4.title')}
          subtitle={t('onboardingJourney.step4.subtitle')}
          eta={t('onboardingJourney.step4.eta')}
        >
          <LaunchStepContent published={published} url={storefrontUrl} />
        </JourneyStep>

        {payoutState === 'done' && active && !published ? (
          <div className="text-muted-foreground mt-2 flex items-center gap-2 text-xs">
            <Wallet className="h-3.5 w-3.5" />
            {t('onboardingJourney.footerPayouts')}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
